import { useEffect, useState } from "react";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  gridFilteredSortedRowIdsSelector,
} from "@mui/x-data-grid";
import SettingsIcon from "@mui/icons-material/Settings";
import { Stack, Typography, Alert, Box, Divider } from "@mui/material";

const getRowsToExport = ({ apiRef }) =>
  gridFilteredSortedRowIdsSelector(apiRef);

const exportOptions = { getRowsToExport };

function StudentDetailsToolbar({
  CustomToolbarComponent,
  CustomToolbarActions,
}) {
  return (
    <GridToolbarContainer
      sx={{ p: 1, gap: 1, flexWrap: "wrap", alignItems: "center" }}
    >
      {CustomToolbarComponent && (
        <>
          <CustomToolbarComponent />
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        </>
      )}
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExport
        csvOptions={exportOptions}
        printOptions={exportOptions}
      />
      {CustomToolbarActions && <CustomToolbarActions />}
      <Box sx={{ flexGrow: 1 }} />
      <GridToolbarQuickFilter debounceMs={500} />
    </GridToolbarContainer>
  );
}

const gridStyle = {
  mb: 7,
  "& .MuiDataGrid-main": { paddingBottom: "20px" },
  ".MuiDataGrid-row": {
    background: "#FEFBFF",
    borderbottom: "1px solid #767680",
  },
  "& .rightAlignedCell": {
    textAlign: "right",
    justifyContent: "flex-end",
    display: "flex",
  },
  "& .MuiDataGrid-cell": {
    display: "flex !important",
    alignItems: "center !important",
  },
  "& .MuiDataGrid-columnHeaderGroup": {
    justifyContent: "center !important",
  },
  "& .MuiDataGrid-columnHeaderTitleContainer": {
    justifyContent: "center !important",
    width: "100%",
  },
  "& .MuiDataGrid-columnHeaderGroup .MuiDataGrid-columnHeaderTitle": {
    textAlign: "center",
    fontWeight: "bold",
    width: "100%",
    justifyContent: "center !important",
  },
};

const CustomNoRowsOverlay = () => (
  <Stack height="100%" alignItems="center" justifyContent="center">
    <Typography variant="body2" color="textSecondary">
      No rows
    </Typography>
  </Stack>
);

const CustomErrorOverlay = ({ error }) => (
  <Stack
    height="100%"
    alignItems="center"
    justifyContent="center"
    sx={{ p: 2 }}
  >
    <Alert severity="error" sx={{ maxWidth: 600 }}>
      {error}
    </Alert>
  </Stack>
);

function GridIndexStudentDetails({
  rows,
  columns,
  rowCount = 0,
  page,
  pageSize,
  handleOnPageChange,
  handleOnPageSizeChange,
  loading,
  getRowClassName,
  handleOnFilterChange,
  columnVisibilityModel = {},
  setColumnVisibilityModel = () => {},
  onRowClick = () => {},
  error = null,
  disableRowSelectionOnClick = true,
  CustomToolbarComponent = null,
  CustomToolbarActions = null,
  ...props
}) {
  const [updatePageSize, setUpdatePageSize] = useState(pageSize);

  useEffect(() => {
    setUpdatePageSize(pageSize);
  }, [pageSize]);

  const CustomButton = () => <SettingsIcon />;
  const overlayComponent = error
    ? () => <CustomErrorOverlay error={error} />
    : CustomNoRowsOverlay;

  if (pageSize) {
    props.rowCount = rowCount;
    props.page = page;
    props.paginationMode = "server";
    props.onPaginationModelChange = (model) => handleOnPageChange(model.page);
    props.onPageSizeChange = handleOnPageSizeChange;
    props.filterMode = "server";
    props.onFilterModelChange = handleOnFilterChange;
  } else {
    props.onPageSizeChange = (newPageSize) => setUpdatePageSize(newPageSize);
  }

  return (
    <DataGrid
      autoHeight
      rows={rows}
      columns={columns}
      getRowId={(row) => row.id}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={(newModel) =>
        setColumnVisibilityModel(newModel)
      }
      disableRowSelectionOnClick={disableRowSelectionOnClick}
      slots={{
        toolbar: StudentDetailsToolbar,
        moreActionsIcon: CustomButton,
        noRowsOverlay: overlayComponent,
      }}
      slotProps={{
        toolbar: {
          showQuickFilter: true,
          quickFilterProps: { debounceMs: 500 },
          csvOptions: { getRowsToExport },
          printOptions: { getRowsToExport },
          CustomToolbarComponent,
          CustomToolbarActions,
        },
      }}
      sx={{ ...gridStyle, ...props.sx }}
      pageSizeOptions={[50, 100]}
      pageSize={updatePageSize || 100}
      scrollbarSize={0}
      density="compact"
      loading={loading}
      getRowClassName={getRowClassName}
      onRowClick={onRowClick}
      {...props}
    />
  );
}

export default GridIndexStudentDetails;
